import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft } from "lucide-react";

const AthleteDealSubmit = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [athleteId, setAthleteId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    brand_name: "",
    deal_type: "",
    amount: "",
    start_date: "",
    end_date: "",
    description: "",
    deliverables: "",
    brand_contact_email: "",
    brand_contact_phone: "",
  });

  useEffect(() => {
    const checkAthleteProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: athleteProfile } = await supabase
        .from("athlete_profiles")
        .select("athlete_id")
        .eq("user_id", user.id)
        .single();

      if (athleteProfile) {
        setAthleteId(athleteProfile.athlete_id);
      } else {
        navigate("/dashboard");
      }
    };

    checkAthleteProfile();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!athleteId) return;

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Fetch high-value threshold setting
      const { data: settings } = await supabase
        .from("institution_settings")
        .select("high_value_threshold, auto_analyze_enabled")
        .limit(1)
        .maybeSingle();

      const highValueThreshold = (settings as any)?.high_value_threshold || 10000;
      const dealAmount = parseFloat(formData.amount);
      const isHighValue = dealAmount >= highValueThreshold;
      
      const { error } = await supabase.from("deals").insert({
        ...formData,
        athlete_id: athleteId,
        amount: dealAmount,
        status: "pending_compliance",
        submitted_by: user?.id,
      });

      if (error) throw error;

      // Show appropriate toast based on deal value
      if (isHighValue && (settings as any)?.auto_analyze_enabled !== false) {
        toast({
          title: "High-Value Deal Submitted",
          description: `Your deal has been submitted for compliance review. Because this is a high-value deal ($${dealAmount.toLocaleString()}), it will be automatically analyzed by our AI system within a few seconds.`,
          duration: 7000,
        });
      } else {
        toast({
          title: "Success",
          description: "Deal submitted successfully and is pending compliance review",
        });
      }

      navigate("/athlete/portal");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/athlete/portal")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Submit New Deal</h1>
            <p className="text-muted-foreground mt-2">
              Submit your NIL deal for compliance review
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Deal Information</CardTitle>
            <CardDescription>
              Please provide all required information about your NIL deal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Deal Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => updateFormData("title", e.target.value)}
                  placeholder="e.g., Brand Ambassador for Local Business"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="brand_name">Brand/Company Name *</Label>
                <Input
                  id="brand_name"
                  value={formData.brand_name}
                  onChange={(e) => updateFormData("brand_name", e.target.value)}
                  placeholder="Company or brand name"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deal_type">Deal Type *</Label>
                  <Select
                    value={formData.deal_type}
                    onValueChange={(value) => updateFormData("deal_type", value)}
                    required
                  >
                    <SelectTrigger id="deal_type">
                      <SelectValue placeholder="Select deal type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="endorsement">Endorsement</SelectItem>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="appearance">Appearance</SelectItem>
                      <SelectItem value="merchandise">Merchandise</SelectItem>
                      <SelectItem value="autograph_signing">Autograph Signing</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Compensation Amount ($) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => updateFormData("amount", e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => updateFormData("start_date", e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => updateFormData("end_date", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => updateFormData("description", e.target.value)}
                  placeholder="Describe the nature of this deal..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="deliverables">Deliverables</Label>
                <Textarea
                  id="deliverables"
                  value={formData.deliverables}
                  onChange={(e) => updateFormData("deliverables", e.target.value)}
                  placeholder="What are you required to do? (e.g., 2 Instagram posts per month, 1 event appearance)"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand_contact_email">Brand Contact Email</Label>
                  <Input
                    id="brand_contact_email"
                    type="email"
                    value={formData.brand_contact_email}
                    onChange={(e) => updateFormData("brand_contact_email", e.target.value)}
                    placeholder="contact@brand.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="brand_contact_phone">Brand Contact Phone</Label>
                  <Input
                    id="brand_contact_phone"
                    type="tel"
                    value={formData.brand_contact_phone}
                    onChange={(e) => updateFormData("brand_contact_phone", e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/athlete/portal")}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="flex-1">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Deal"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AthleteDealSubmit;
